import React, { useState, useCallback, useEffect, useRef } from "react";
import { cn } from "@/utils/index";
import { DownArrowIcon } from "../data-display/Icon";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/router";

function PriceRange({ prices }) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [minPrice, setMinPrice] = useState(prices.min);
  const [maxPrice, setMaxPrice] = useState(prices.max);
  const [isDragging, setIsDragging] = useState(null);
  const trackRef = useRef(null);
  const router = useRouter();
  const debounceTimer = useRef(null);

  const baseMinPrice = prices.min;
  const baseMaxPrice = prices.max;

  useEffect(() => {
    if (router.query.min_price && router.query.max_price) {
      setMinPrice(parseInt(router.query.min_price));
      setMaxPrice(parseInt(router.query.max_price));
      setIsFilterOpen(true);
    }
  }, [router]);

  const pushToRouter = useCallback(
    (min, max) => {
      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set new timer
      debounceTimer.current = setTimeout(() => {
        const currentQuery = { ...router.query };
        currentQuery.min_price = min.toString();
        currentQuery.max_price = max.toString();
        currentQuery.page = "1";

        router.push(
          {
            pathname: router.pathname,
            query: currentQuery,
          },
          undefined,
          { shallow: true }
        );
      }, 300);
    },
    [router]
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleStart = (thumb) => (e) => {
    e.preventDefault();
    setIsDragging(thumb);
  };

  const getPositionFromEvent = useCallback((e) => {
    if (!trackRef.current) return 0;
    const track = trackRef.current;
    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    return Math.max(0, Math.min(clientX - rect.left, rect.width));
  }, []);

  const handleMove = useCallback(
    (e) => {
      if (!isDragging || !trackRef.current) return;

      const x = getPositionFromEvent(e);
      // Clamp percentage between 0 and 1
      const percentage = Math.min(
        Math.max(0, x / trackRef.current.clientWidth),
        1
      );

      // Calculate value with bounds
      const value = Math.min(
        Math.max(
          baseMinPrice,
          Math.round(percentage * (baseMaxPrice - baseMinPrice) + baseMinPrice)
        ),
        baseMaxPrice
      );

      let newMinPrice = minPrice;
      let newMaxPrice = maxPrice;

      if (isDragging === "min") {
        newMinPrice = Math.min(value, maxPrice - 1);
        setMinPrice(newMinPrice);
      } else {
        newMaxPrice = Math.min(Math.max(value, minPrice + 1), baseMaxPrice);
        setMaxPrice(newMaxPrice);
      }

      pushToRouter(newMinPrice, newMaxPrice);
    },
    [isDragging, minPrice, maxPrice, baseMinPrice, baseMaxPrice]
  );

  const handleEnd = useCallback(() => {
    setIsDragging(null);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMove);
      window.addEventListener("touchmove", handleMove);
      window.addEventListener("mouseup", handleEnd);
      window.addEventListener("touchend", handleEnd);

      return () => {
        window.removeEventListener("mousemove", handleMove);
        window.removeEventListener("touchmove", handleMove);
        window.removeEventListener("mouseup", handleEnd);
        window.removeEventListener("touchend", handleEnd);
      };
    }
  }, [isDragging, handleMove, handleEnd]);

  const handleTrackClick = (e) => {
    const track = trackRef.current;
    if (!track) return;

    const rect = track.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = Math.min(Math.max(0, x / rect.width), 1);

    const value = Math.min(
      Math.max(
        baseMinPrice,
        Math.round(percentage * (baseMaxPrice - baseMinPrice) + baseMinPrice)
      ),
      baseMaxPrice
    );

    const distToMin = Math.abs(value - minPrice);
    const distToMax = Math.abs(value - maxPrice);

    let newMinPrice = minPrice;
    let newMaxPrice = maxPrice;

    if (distToMin < distToMax) {
      newMinPrice = Math.min(value, maxPrice - 1);
      setMinPrice(newMinPrice);
    } else {
      newMaxPrice = Math.min(Math.max(value, minPrice + 1), baseMaxPrice);
      setMaxPrice(newMaxPrice);
    }

    pushToRouter(newMinPrice, newMaxPrice);
  };

  const getLeftPosition = (value) => {
    const percentage =
      ((value - baseMinPrice) / (baseMaxPrice - baseMinPrice)) * 100;
    return Math.min(Math.max(0, percentage), 100); // Clamp between 0-100%
  };

  const transition = { type: "spring", stiffness: 200, damping: 15, mass: 0.5 };

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsFilterOpen(!isFilterOpen)}
        className={cn(
          "flex w-full items-center justify-between text-left font-medium py-[10px] px-[20px] bg-[#EBEEF2]",
          isFilterOpen ? "rounded-t-xl" : "rounded-xl"
        )}
      >
        <span className="text-lg font-medium text-gray-800 w-full line-clamp-1">
          Price Range
        </span>
        <DownArrowIcon />
      </button>

      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="w-full bg-white border rounded-b-xl border-[#EBEEF2] max-h-[340px]"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="p-4 space-y-8 ">
              <div
                className="relative h-2 bg-gray-200 rounded-full cursor-pointer"
                ref={trackRef}
                onClick={handleTrackClick}
              >
                <motion.div
                  className="absolute inset-y-0 bg-[#E81E61] rounded-full"
                  initial={false}
                  animate={{
                    left: `${getLeftPosition(minPrice)}%`,
                    right: `${100 - getLeftPosition(maxPrice)}%`,
                  }}
                  transition={transition}
                />

                <motion.div
                  onMouseDown={handleStart("min")}
                  onTouchStart={handleStart("min")}
                  className={cn(
                    "absolute w-5 h-5 bg-white rounded-full shadow-md top-[-6px] border-2 border-[#E81E61] flex items-center justify-center touch-none",
                    isDragging === "min" && "scale-110"
                  )}
                  initial={false}
                  animate={{
                    left: `calc(${getLeftPosition(minPrice)}% - 0px)`,
                    scale: isDragging === "min" ? 1.2 : 1,
                  }}
                  transition={transition}
                >
                  <div className="w-2 h-2 bg-[#E81E61] rounded-full" />
                </motion.div>

                <motion.div
                  onMouseDown={handleStart("max")}
                  onTouchStart={handleStart("max")}
                  className={cn(
                    "absolute w-5 h-5 bg-white rounded-full shadow-md top-[-6px] border-2 border-[#E81E61] flex items-center justify-center touch-none",
                    isDragging === "max" && "scale-110"
                  )}
                  initial={false}
                  animate={{
                    left: `calc(${getLeftPosition(maxPrice)}% - 12px)`,
                    scale: isDragging === "max" ? 1.2 : 1,
                  }}
                  transition={transition}
                >
                  <div className="w-2 h-2 bg-[#E81E61] rounded-full" />
                </motion.div>
              </div>

              <div className="flex justify-between gap-4">
                <p className="p-2 bg-[#EBEEF2] text-center w-1/2 rounded text-base font-medium">
                  &#2547; {minPrice}
                </p>
                <p className="p-2 bg-[#EBEEF2] text-center w-1/2 rounded text-base font-medium">
                  &#2547; {maxPrice}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default PriceRange;
